import React, { useRef } from "react";

type MagnetProps = Omit<React.HTMLAttributes<HTMLDivElement>, "style"> & {
  children: React.ReactNode;
  padding?: number;
  disabled?: boolean;
  magnetStrength?: number;
  wrapperClassName?: string;
  innerClassName?: string;
};

export function Magnet({
  children,
  padding = 100,
  disabled = false,
  magnetStrength = 2,
  wrapperClassName = "",
  innerClassName = "",
  ...props
}: MagnetProps) {
  const magnetRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const rectRef = useRef<DOMRect | null>(null);

  const moveMagnet = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || !contentRef.current || !rectRef.current) return;

    const rect = rectRef.current;
    const distanceX = event.clientX - (rect.left + rect.width / 2);
    const distanceY = event.clientY - (rect.top + rect.height / 2);
    const isWithinRange =
      Math.abs(distanceX) < rect.width / 2 + padding &&
      Math.abs(distanceY) < rect.height / 2 + padding;

    contentRef.current.style.transitionDuration = isWithinRange ? "300ms" : "500ms";
    contentRef.current.style.transform = isWithinRange
      ? `translate3d(${distanceX / magnetStrength}px, ${distanceY / magnetStrength}px, 0)`
      : "translate3d(0, 0, 0)";
  };

  const resetMagnet = () => {
    rectRef.current = null;
    if (!contentRef.current) return;
    contentRef.current.style.transitionDuration = "500ms";
    contentRef.current.style.transform = "translate3d(0, 0, 0)";
  };

  return (
    <div
      ref={magnetRef}
      className={`magnet-wrapper ${wrapperClassName}`}
      onPointerEnter={() => {
        if (!disabled) rectRef.current = magnetRef.current?.getBoundingClientRect() ?? null;
      }}
      onPointerMove={moveMagnet}
      onPointerLeave={resetMagnet}
      {...props}
    >
      <div
        ref={contentRef}
        className={`magnet-content ${innerClassName}`}
        style={{
          transitionProperty: "transform",
          transitionTimingFunction: "ease-out",
          willChange: disabled ? undefined : "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
}
